import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { TaskListTemplateModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/taskListTemplate/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/taskListTemplate/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/taskListTemplate/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/taskListTemplate/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/taskListTemplate/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/taskListTemplate/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/taskListTemplate/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/taskListTemplate/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/taskListTemplate/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/taskListTemplate/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/taskListTemplate/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/taskListTemplate/DELETE_FAILURE'

export type TaskListTemplateModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: TaskListTemplateModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedIds: [],
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadTaskListTemplates(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().taskListTemplates.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/task_list_templates.json', options)
      .then(({ data }: { data: TaskListTemplateModel[] }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadTaskListTemplate(taskListTemplateId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedIds = [...state.taskListTemplates.loadedIds]
    const loadedForKeys = [...state.taskListTemplates.loadedForKeys]

    if (taskListTemplateId && !loadedIds.includes(taskListTemplateId)){
      loadedIds.push(taskListTemplateId)
    }

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/task_list_templates/${taskListTemplateId}.json`, options)
      .then(({ data }: { data: TaskListTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createTaskListTemplate(taskListTemplate: Partial<TaskListTemplateModel>, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      task_list_template: taskListTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/task_list_templates.json', options, config)
      .then(({ data }: { data: TaskListTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateTaskListTemplate(
  taskListTemplate: UpdateParams<TaskListTemplateModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      task_list_template: taskListTemplate,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/task_list_templates/${taskListTemplate.id}.json`, options, config)
      .then(({ data }: { data: TaskListTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateTaskListTemplate(taskListTemplate: Partial<TaskListTemplateModel>) {
  const { id } = taskListTemplate

  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/task_list_templates/${id}/duplicate.json`, {}, config)
      .then(({ data }: { data: TaskListTemplateModel }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteTaskListTemplate(taskListTemplate: DeleteParams<TaskListTemplateModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/task_list_templates/${taskListTemplate.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.TASK_LIST_TEMPLATE)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadTemplateQualityControlCategories(options: EntityOptions = {}) {
  const config = {
    method: 'GET',
  }

  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api('/task_list_templates/template_qc_categories.json', options, config)
      // @ts-expect-error TS7008
      .then(({ data }): { success: true, data } => {
        // @ts-expect-error TS2554
        dispatch(fetchSuccess({ data }))

        return { success: true, data: data.data }
      })
      .catch((data): { success: false, errors: string[] } => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: TaskListTemplateModuleState = initialState,
  action: Action = { type: '' },
): TaskListTemplateModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
